import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import { GraphemeId, Word, WordId } from '@/common/types/scopeAndSequence';
import { getGraphemeById, getWordById } from '@/common/utils/scopeAndSequence';
import _ from 'lodash';

function containsVowel(spelling: string): boolean {
  const vowels = ['a', 'e', 'i', 'o', 'u'];
  return vowels.some((vowel) => spelling.includes(vowel));
}

export function getRandomSpellings(
  spellings: string[],
  exclude: string[] = [],
): string[] {
  let selectedSpellings: string[] = exclude.slice();
  let vowelsIncluded = selectedSpellings.some(containsVowel);

  while (selectedSpellings.length < 7) {
    const randomIndex = Math.floor(Math.random() * spellings.length);
    const selectedSpelling = spellings[randomIndex];

    if (!selectedSpellings.includes(selectedSpelling)) {
      selectedSpellings.push(selectedSpelling);
      if (containsVowel(selectedSpelling)) {
        vowelsIncluded = true;
      }
    }

    if (selectedSpellings.length === 6 && !vowelsIncluded) {
      selectedSpellings.pop(); // Remove the last spelling
    }
  }
  //randomize
  selectedSpellings = selectedSpellings.sort(() => Math.random() - 0.5);
  return selectedSpellings;
}

export function getWordFromLevel(level: number): Word {
  let wordSet = getWordSetFromLevel(level);

  const randomWord = _.sample(wordSet);
  if (!randomWord) {
    throw new Error(`No word found in word set for level ${level}.`);
  }
  return randomWord;
}

export function getWordSetFromLevel(level: number): Word[] {
  let levelData = scopeAndSequence.levels.find((lvl) => lvl.level === level);
  if (!levelData) {
    throw new Error(`Level ${level} not found in scope and sequence data.`);
  }

  let wordSet = levelData.wordSet;
  let backTrackLevelsCount = 1;
  while (wordSet.length === 0) {
    // Try and get the word set for the previous level.
    levelData = scopeAndSequence.levels.find(
      (lvl) => lvl.level === level - backTrackLevelsCount,
    );
    if (levelData) {
      wordSet = levelData.wordSet;
    }
    backTrackLevelsCount -= 1;
    if (level - backTrackLevelsCount < 0) {
      // we're out of possible levels.
      throw new Error(
        `Couldn't find viable level below ${level} to draw word sets from!`,
      );
    }
  }
  if (!levelData) {
    throw new Error(`Level ${level} not found in scope and sequence data.`);
  }

  return levelData.wordSet.map(
    (wordId) => getWordById(wordId as WordId) as Word,
  );
}

export function makeSpellingListFromLevel(level: number) {
  level = Math.max(level, 3); // Draw letters from the first 3 levels so that we will always have enough.
  const levelData = scopeAndSequence.levels.filter((lvl) => lvl.level <= level);
  const spellings = levelData.map((lvl) => {
    const levelSpellings: string[] = [];
    for (const graphemeId of lvl.newGraphemes) {
      const grapheme = getGraphemeById(graphemeId as GraphemeId);
      if (grapheme && !grapheme.spelling.includes('_')) {
        levelSpellings.push(grapheme.spelling);
      }
    }
    return levelSpellings;
  });
  return spellings.flat();
}
